import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SystemModal from 'components/Shared/Modal';
import AddCardForm from './AddCardForm';
import { ScaVerificationModal, ScaOrderChallengeModal } from 'pages/PaymentsSpa/modals';
import ScaOrderInitiatedIframe from 'pages/PaymentsSpa/PASFormView/ScaOrderIntiatedIframe';
import {
  useCreateSharedPayment,
  useRefreshPaymentMethods,
} from 'services/reactQueryHooks/payments';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

const AddCardModal = ({ visible, onClose }) => {
  const { translateText } = useTranslation();
  const { corporateClientId, currentCorporation } = useSelector(state => state.user);
  const { refreshPaymentMethods } = useRefreshPaymentMethods();

  const [showVerification, setShowVerification] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  // A card added here funds the Wallet, so drivers do not get it unless this is ticked
  const [sharedWithDrivers, setSharedWithDrivers] = useState(false);

  const {
    mutate: createPayment,
    isLoading,
    isError,
    isSuccess,
    isSharingError,
    paymentAccountId,
    scaChallenge,
    orderInitiated,
    reset,
  } = useCreateSharedPayment({
    onReset: () => {
      setShowChallenge(false);
      setShowVerification(false);
    },
    sharedWithDrivers,
  });

  const handleSubmit = payload => {
    createPayment({ card: payload, restrictionType: currentCorporation?.restrictionType });
  };

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // This component stays mounted between opens, so the previous card's choice would otherwise be
  // the default for the next one. Every add starts from "not available to drivers" again.
  useEffect(() => {
    if (visible) {
      setSharedWithDrivers(false);
    }
  }, [visible]);

  useEffect(() => {
    if (scaChallenge) {
      setShowVerification(true);
    }
  }, [orderInitiated, scaChallenge]);

  useEffect(() => {
    if (isSuccess) {
      // The new card can take a few seconds to turn up in GET /payments, so this polls the list
      // until it does instead of reading it once.
      if (!isSharingError) {
        refreshPaymentMethods(paymentAccountId, sharedWithDrivers);
      }
      handleClose();
    }
  }, [
    isSuccess,
    isSharingError,
    sharedWithDrivers,
    paymentAccountId,
    refreshPaymentMethods,
    handleClose,
  ]);

  useEffect(() => {
    if (isError) {
      reset();
    }
  }, [isError, reset]);

  return (
    <SystemModal
      title={
        <div>
          <div className={styles.dashboardModalTitle}>
            {translateText('corporateAccounts.wallet.dashboard.addFunds.addNewCard')}
          </div>
          <div className={styles.dashboardModalSubtitle}>
            {translateText('corporateAccounts.wallet.dashboard.addFunds.addNewCardDescription')}
          </div>
        </div>
      }
      titleLevel={5}
      width={440}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      bodyStyle={{ padding: '20px' }}
      className={styles.dashboardModal}
      destroyOnClose
      centered
      data-testid="addCardModal"
    >
      <ScaVerificationModal
        onOk={() => setShowChallenge(true)}
        onCancel={reset}
        visible={showVerification}
      />
      {showChallenge && (
        <ScaOrderChallengeModal
          visible={showChallenge}
          challengeHtml={scaChallenge}
          onCancel={reset}
        />
      )}
      {orderInitiated && <ScaOrderInitiatedIframe gatewayHtml={orderInitiated} />}
      <AddCardForm
        sharedWithDrivers={sharedWithDrivers}
        onSharedWithDriversChange={setSharedWithDrivers}
        corporateClientId={corporateClientId}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        submitting={isLoading}
        language={currentCorporation?.preferredLanguage}
      />
    </SystemModal>
  );
};

export default AddCardModal;
